import React from 'react';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import visionService, { VisionMatch } from '../services/visionService';

type FaceBox = { x: number; y: number; w: number; h: number }; // stage px (po object-fit: cover)
type FaceBoxNorm = { minX: number; minY: number; maxX: number; maxY: number }; // 0..1 w przestrzeni video
type Track = {
  id: string;
  box: FaceBox; // wygładzony
  target: FaceBox; // cel
  norm: FaceBoxNorm; // do cropów
  label: string; // imię+nazwisko albo "Nierozpoznano"
  score: number | null; // dopasowanie (0..1)
  threshold: number | null;
  lastSeenAt: number;
};

const MIRROR_PREVIEW = true;

const VisionPage: React.FC = () => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const [cameraOn, setCameraOn] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [tracks, setTracks] = React.useState<Track[]>([]);
  const [status, setStatus] = React.useState<string>('—');
  const [threshold, setThreshold] = React.useState<number | null>(null);
  const inFlightRef = React.useRef(false);
  const tracksRef = React.useRef<Track[]>([]);

  const landmarkerRef = React.useRef<any>(null);
  const [mpReady, setMpReady] = React.useState(false);
  const mpBusyRef = React.useRef(false);
  const nextIdRef = React.useRef(1);

  React.useEffect(() => {
    tracksRef.current = tracks;
  }, [tracks]);

  const startCamera = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOn(true);

      // Lazy-init MediaPipe (web) tylko do bbox twarzy (tag "przyklejony")
      if (!landmarkerRef.current) {
        try {
          const vision = await import('@mediapipe/tasks-vision');
          const FilesetResolver = (vision as any).FilesetResolver;
          const FaceLandmarker = (vision as any).FaceLandmarker;
          const fileset = await FilesetResolver.forVisionTasks(
            'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/wasm'
          );
          landmarkerRef.current = await FaceLandmarker.createFromOptions(fileset, {
            baseOptions: {
              modelAssetPath:
                'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
            },
            runningMode: 'VIDEO',
            numFaces: 6,
            outputFaceBlendshapes: false,
            outputFacialTransformationMatrixes: false,
          });
          setMpReady(true);
        } catch (e: any) {
          setMpReady(false);
          setError(e?.message || 'Nie udało się załadować MediaPipe (tag na twarzy).');
        }
      } else {
        setMpReady(true);
      }
    } catch (e: any) {
      setCameraOn(false);
      setError(e?.message || 'Nie udało się uruchomić kamery. Sprawdź uprawnienia w przeglądarce.');
    }
  };

  const stopCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch {}
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch {}
      // @ts-ignore
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
    setTracks([]);
    setStatus('—');
    setMpReady(false);
  };

  const captureFrame = async (): Promise<Blob | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85));
    return blob;
  };

  React.useEffect(() => {
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!cameraOn) return;

    const t = window.setInterval(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        // Rozpoznanie co ~1s: wyślij cropy aktualnie widocznych twarzy
        const current = tracksRef.current;
        if (!current.length) {
          setStatus('Brak twarzy');
          return;
        }
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const w = video.videoWidth || 0;
        const h = video.videoHeight || 0;
        if (!w || !h) return;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, w, h);

        const crops: Blob[] = [];
        const ids: string[] = [];
        for (const tr of current) {
          const n = tr.norm;
          // marginesy
          // większy margines, żeby backendowe wykrywanie twarzy było stabilne na cropie
          const padX = (n.maxX - n.minX) * 0.30;
          const padY = (n.maxY - n.minY) * 0.35;
          const x0 = Math.max(0, (n.minX - padX) * w);
          const y0 = Math.max(0, (n.minY - padY) * h);
          const x1 = Math.min(w, (n.maxX + padX) * w);
          const y1 = Math.min(h, (n.maxY + padY) * h);
          const cw = Math.max(24, x1 - x0);
          const ch = Math.max(24, y1 - y0);

          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = Math.round(cw);
          cropCanvas.height = Math.round(ch);
          const cctx = cropCanvas.getContext('2d');
          if (!cctx) continue;
          cctx.drawImage(canvas, x0, y0, cw, ch, 0, 0, cw, ch);

          // eslint-disable-next-line no-await-in-loop
          const blob: Blob | null = await new Promise((resolve) => cropCanvas.toBlob((b) => resolve(b), 'image/jpeg', 0.86));
          if (!blob) continue;
          crops.push(blob);
          ids.push(tr.id);
        }

        if (!crops.length) return;

        setStatus('Skanuję…');
        const res = await visionService.recognizeBatch(crops);
        if (typeof res?.threshold === 'number') setThreshold(res.threshold);

        // aktualizuj label per track
        setTracks((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          for (const r of res.results || []) {
            const id = ids[r.index];
            if (!id) continue;
            const prevT = map.get(id);
            if (!prevT) continue;

            if (r.match) {
              map.set(id, {
                ...prevT,
                label: `${r.match.first_name} ${r.match.last_name}`,
                score: r.match.score,
                threshold: res.threshold,
              });
            } else {
              map.set(id, {
                ...prevT,
                label: 'Nierozpoznano',
                score: (typeof r.best_score === 'number' ? r.best_score : null),
                threshold: res.threshold,
              });
            }
          }
          return Array.from(map.values());
        });

        setStatus('OK');
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Błąd rozpoznawania');
      } finally {
        inFlightRef.current = false;
      }
    }, 900);

    return () => window.clearInterval(t);
  }, [cameraOn]);

  const bboxFromLandmarks = (landmarks: Array<{ x: number; y: number }>): FaceBoxNorm => {
    let minX = 1, minY = 1, maxX = 0, maxY = 0;
    for (const p of landmarks) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return { minX, minY, maxX, maxY };
  };

  const mapToCoverCoords = (videoW: number, videoH: number, stageW: number, stageH: number, box: { minX: number; minY: number; maxX: number; maxY: number }): FaceBox => {
    // video coords
    const x0 = box.minX * videoW;
    const y0 = box.minY * videoH;
    const x1 = box.maxX * videoW;
    const y1 = box.maxY * videoH;

    // object-fit: cover mapping
    const scale = Math.max(stageW / videoW, stageH / videoH);
    const dispW = videoW * scale;
    const dispH = videoH * scale;
    const offX = (stageW - dispW) / 2;
    const offY = (stageH - dispH) / 2;

    const X0 = x0 * scale + offX;
    const Y0 = y0 * scale + offY;
    const X1 = x1 * scale + offX;
    const Y1 = y1 * scale + offY;

    return { x: X0, y: Y0, w: X1 - X0, h: Y1 - Y0 };
  };

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const lerpBox = (cur: FaceBox, target: FaceBox, t: number): FaceBox => ({
    x: lerp(cur.x, target.x, t),
    y: lerp(cur.y, target.y, t),
    w: lerp(cur.w, target.w, t),
    h: lerp(cur.h, target.h, t),
  });

  const dist2 = (ax: number, ay: number, bx: number, by: number) => {
    const dx = ax - bx;
    const dy = ay - by;
    return dx * dx + dy * dy;
  };

  // Detekcja twarzy (wiele) + tracking (płynnie) do "przyklejenia" dymków
  React.useEffect(() => {
    if (!cameraOn) return;
    if (!mpReady || !videoRef.current || !landmarkerRef.current) return;

    let cancelled = false;
    const interval = window.setInterval(() => {
      if (cancelled) return;
      if (mpBusyRef.current) return;
      mpBusyRef.current = true;
      try {
        const video = videoRef.current;
        const stage = stageRef.current;
        if (!video || !stage) return;
        const vw = video.videoWidth || 0;
        const vh = video.videoHeight || 0;
        const sw = stage.clientWidth || 0;
        const sh = stage.clientHeight || 0;
        if (!vw || !vh || !sw || !sh) return;

        const now = performance.now();
        const res = landmarkerRef.current.detectForVideo(video, now);
        const faces = Array.isArray(res?.faceLandmarks) ? res.faceLandmarks : [];
        const nowTs = Date.now();
        if (!faces.length) {
          // wygaszaj po 1s braku
          setTracks((prev) => prev.filter((t) => (nowTs - t.lastSeenAt) < 1000));
          return;
        }

        const detections = faces.map((lm: any[]) => {
          const norm = bboxFromLandmarks(lm as any);
          const stageBox = mapToCoverCoords(vw, vh, sw, sh, norm);
          const cx = stageBox.x + stageBox.w / 2;
          const cy = stageBox.y + stageBox.h / 2;
          return { norm, stageBox, cx, cy };
        });

        setTracks((prev) => {
          const used = new Set<string>();
          const next: Track[] = [];

          // dopasuj do istniejących po najbliższym środku (greedy)
          for (const det of detections) {
            let bestId: string | null = null;
            let bestD = Infinity;
            for (const tr of prev) {
              if (used.has(tr.id)) continue;
              const tcx = tr.target.x + tr.target.w / 2;
              const tcy = tr.target.y + tr.target.h / 2;
              const d = dist2(det.cx, det.cy, tcx, tcy);
              if (d < bestD) {
                bestD = d;
                bestId = tr.id;
              }
            }

            const maxAssignDist2 = Math.pow(Math.max(60, det.stageBox.h * 0.9), 2);
            if (!bestId || bestD > maxAssignDist2) {
              // nowy track
              const id = String(nextIdRef.current++);
              next.push({
                id,
                box: det.stageBox,
                target: det.stageBox,
                norm: det.norm,
                label: 'Nierozpoznano',
                score: null,
                threshold: threshold,
                lastSeenAt: nowTs,
              });
            } else {
              used.add(bestId);
              const old = prev.find((p) => p.id === bestId)!;
              next.push({
                ...old,
                target: det.stageBox,
                box: lerpBox(old.box, det.stageBox, 0.35),
                norm: det.norm,
                lastSeenAt: nowTs,
              });
            }
          }

          // zachowaj te, które zniknęły chwilowo (do 1s), ale dalej je wygładzaj do targetu
          for (const tr of prev) {
            if (next.find((n) => n.id === tr.id)) continue;
            if ((nowTs - tr.lastSeenAt) < 1000) {
              next.push({ ...tr, box: lerpBox(tr.box, tr.target, 0.25) });
            }
          }

          return next;
        });
      } finally {
        mpBusyRef.current = false;
      }
    }, 120);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [cameraOn, mpReady, threshold]);

  return (
    <Box
      sx={{
        bgcolor: '#FAFAFA',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Typography
          component="h1"
          sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '20px', lineHeight: '24px', fontWeight: 400, color: '#020242', m: 0, mt: '8.5px' }}
        >
          Vision – rozpoznawanie twarzy (kamera)
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Paper elevation={1} sx={{ p: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {!cameraOn ? (
            <Button variant="outlined" onClick={startCamera}>Włącz kamerę</Button>
          ) : (
            <Button variant="text" onClick={stopCamera}>Wyłącz kamerę</Button>
          )}
          <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
            Status: {status}
          </Typography>
          {cameraOn && (
            <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
              {mpReady ? 'Detekcja: OK' : 'Detekcja: ładowanie…'}
            </Typography>
          )}
        </Box>

        <Box
          ref={stageRef}
          sx={{
            position: 'relative',
            width: '100%',
            flex: 1,
            minHeight: 0,
            border: '1px solid #E6E6EC',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: '#000',
            transform: MIRROR_PREVIEW ? 'scaleX(-1)' : 'none',
            transformOrigin: 'center',
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />

          {tracks.map((tr) => {
            const isKnown = tr.label !== 'Nierozpoznano';
            const thr = tr.threshold ?? threshold;
            const thrPct = thr == null ? null : Math.round(Math.max(0, Math.min(1, thr)) * 100);
            const pct = tr.score == null ? null : Math.round(Math.max(0, Math.min(1, tr.score)) * 100);

            return (
              <Box
                key={tr.id}
                sx={{
                  position: 'absolute',
                  left: tr.box.x + tr.box.w / 2,
                  // wyżej nad głową
                  top: Math.max(8, tr.box.y - Math.max(22, tr.box.h * 0.60)),
                  // kontener jest "mirror", więc odwracamy dymek z powrotem dla czytelnego tekstu
                  transform: MIRROR_PREVIEW ? 'translate(-50%, -100%) scaleX(-1)' : 'translate(-50%, -100%)',
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: isKnown ? 'rgba(16, 185, 129, 0.95)' : 'rgba(148, 163, 184, 0.92)',
                  color: isKnown ? '#0b1f14' : '#0b1220',
                  fontSize: 13,
                  fontWeight: 700,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  pointerEvents: 'none',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  transition: 'left 120ms linear, top 120ms linear',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    bottom: -9,
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '9px solid transparent',
                    borderRight: '9px solid transparent',
                    borderTop: `10px solid ${isKnown ? 'rgba(16, 185, 129, 0.95)' : 'rgba(148, 163, 184, 0.92)'}`,
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
                  },
                }}
              >
                <Box sx={{ whiteSpace: 'nowrap' }}>{tr.label}</Box>
                <Box sx={{ fontSize: 11, fontWeight: 600, opacity: 0.9, whiteSpace: 'nowrap', mt: 0.25 }}>
                  {pct == null ? '—' : `${pct}%`}{thrPct == null ? '' : ` / próg ${thrPct}%`}
                </Box>
              </Box>
            );
          })}
        </Box>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Paper>
    </Box>
  );
};

export default VisionPage;

